<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div
      class="modal-container"
      :class="{ 'modal-container--detail': !!activeStudy }"
      @click.stop
    >
      <!-- Grid View -->
      <template v-if="!activeStudy">
        <header class="panel-header">
          <h1 class="panel-title">Use cases</h1>
          <button
            type="button"
            class="modal-close-button"
            @click="$emit('close')"
            aria-label="Close use cases"
          >
            ×
          </button>
        </header>

        <section class="cases-grid" aria-label="Use case cards">
          <div
            v-for="uc in useCases"
            :key="uc.id"
            class="case-card"
            :class="uc.colorClass"
            tabindex="0"
            role="button"
            @click="activeStudy = uc"
            @keydown.enter="activeStudy = uc"
          >
            <img :src="uc.iconSrc" :alt="uc.title" class="case-icon" />
            <p class="case-tag">{{ uc.tag }}</p>
            <h2 class="case-title">{{ uc.title }}</h2>
            <p class="case-copy">{{ uc.blurb }}</p>
          </div>
        </section>
      </template>

      <!-- Detail View -->
      <template v-else>
        <section class="steps-panel" :class="activeStudy.colorClass">
          <button type="button" class="back-button" @click="activeStudy = null">
            &larr; Back to use cases
          </button>

          <section class="intro-block">
            <h1 class="intro-title">{{ activeStudy.title }}</h1>
            <p class="intro-copy">{{ activeStudy.description }}</p>

            <ul class="feature-list">
              <li
                v-for="feature in activeStudy.features"
                :key="feature"
                class="feature-item"
              >
                <img :src="featureIconUrl" alt="" class="feature-icon" />
                <span>{{ feature }}</span>
              </li>
            </ul>

            <p class="intro-note">{{ activeStudy.benefits }}</p>
          </section>

          <h2 class="steps-heading">Steps to build</h2>

          <div class="steps-grid">
            <article
              v-for="(step, i) in activeStudy.steps"
              :key="i"
              class="step-card"
            >
              <p class="step-copy">{{ step.text }}</p>
            </article>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { platformIcon } from '@/config/platformAssets'

// Use-case card icons (top-right of each grid card).
const iconShoppingBasket = platformIcon('shopping-basket.png')
const iconTrophy = platformIcon('trophy.png')
const iconStar = platformIcon('star.png')
const iconBell = platformIcon('bell.png')

// Step-icon URLs used by every use case's step grid.
// Each maps directly to a PNG in s3://{splash}/campaign-creator/icons/.
// Drop new PNGs into that prefix to add or replace any of these.
const stepIcons = {
  star: platformIcon('star.png'),
  pin: platformIcon('pin.png'),
  heart: platformIcon('heart.png'),
  email: platformIcon('email.png'),
  calendar: platformIcon('calendar.png'),
  textbox: platformIcon('textbox.png'),
} as const

// Feature-list bullet icon (left of each feature line in the detail view).
const featureIconUrl = platformIcon('star.png')

interface StepItem {
  text: string
  iconUrl: string
}

interface UseCase {
  id: string
  iconSrc: string
  colorClass: 'green' | 'blue'
  tag: string
  title: string
  blurb: string
  description: string
  benefits: string
  features: string[]
  steps: StepItem[]
}

defineEmits<{ (e: 'close'): void }>()

const useCases: UseCase[] = [
  {
    id: 'instant-ar-rewards',
    iconSrc: iconStar,
    colorClass: 'blue',
    tag: 'Everywhere',
    title: 'Instant AR Rewards',
    blurb:
      'Send customers promotional rewards anywhere, at any time, that can be unlocked instantly from their phone.',
    description:
      'Customers receive promotional rewards they can unlock instantly from their phone. Customers open the experience, discover the 3D object in AR and claim it to reveal the reward or offer.',
    benefits: 'Boost brand engagement, deliver instant value, drive promotional reach.',
    features: [
      'Anywhere availability',
      '24/7 reward unlocks',
      'Instant delivery',
    ],
    steps: [
      {
        text: 'Upload your own 3D object or choose one from our library.',
        iconUrl: stepIcons.star,
      },
      {
        text: 'Apply your branding, colours, and messaging.',
        iconUrl: stepIcons.heart,
      },
      {
        text: 'Upload the reward or offer customers will unlock.',
        iconUrl: stepIcons.email,
      },
      {
        text: 'Choose the start and end dates for your AR experience.',
        iconUrl: stepIcons.calendar,
      },
      {
        text: 'Publish your campaign and share the link via SMS, social, email, or any channel.',
        iconUrl: stepIcons.textbox,
      },
    ],
  },
  {
    id: 'promo-unlock',
    iconSrc: iconBell,
    colorClass: 'blue',
    tag: 'Everywhere',
    title: 'Interactive quiz',
    blurb:
      'Use interactive fun quizzes and surveys to engage audiences at any location, and gather data.',
    description:
      'Attendees open the experience, discover the 3D object in AR and claim it to reveal the reward or offer.',
    benefits:
      'Increase event engagement, reward attendance, amplify sponsor visibility.',
    features: [
      'Anywhere availability',
      '24/7 reward unlocks',
      'Instant delivery',
    ],
    steps: [
      {
        text: 'Upload your own 3D object or choose one from our library.',
        iconUrl: stepIcons.star,
      },
      {
        text: 'Apply your branding, colours, and messaging.',
        iconUrl: stepIcons.heart,
      },
      {
        text: 'Upload the reward or prize attendees will unlock.',
        iconUrl: stepIcons.email,
      },
      {
        text: 'Choose the start and end dates for your AR experience.',
        iconUrl: stepIcons.calendar,
      },
      {
        text: 'Publish your campaign and share the link via SMS, social, email, or any channel.',
        iconUrl: stepIcons.textbox,
      },
    ],
  },
  {
    id: 'retail-ar-hunt',
    iconSrc: iconShoppingBasket,
    colorClass: 'green',
    tag: 'Specific Locations',
    title: 'Retail AR Hunt',
    blurb: 'Place 3D objects at retail locations and reward customers for finding them.',
    description:
      'Customers discover hidden objects on their map, travel to pinned locations, and uncover 3D objects in AR to receive rewards/vouchers (redeemable at POS).',
    benefits:
      'Drive store visits, gamify the shopping experience, increase campaign participation.',
    features: [
      'Adaptable geolocation radius',
      '0\u2013100 Locations',
      'Geotargeted reward delivery',
    ],
    steps: [
      { text: 'Upload your 3D object or choose one from our library.', iconUrl: stepIcons.star },
      {
        text: 'Pinpoint on a map where your audience will engage with the object.',
        iconUrl: stepIcons.pin,
      },
      {
        text: 'Apply your unique branding, colours, and messaging.',
        iconUrl: stepIcons.heart,
      },
      {
        text: 'Upload your voucher and define the reward customers will unlock.',
        iconUrl: stepIcons.email,
      },
      {
        text: 'Choose the start and end dates for your AR experience.',
        iconUrl: stepIcons.calendar,
      },
      {
        text: 'Publish your campaign and share the link via SMS, email, or any channel.',
        iconUrl: stepIcons.textbox,
      },
    ],
  },
  {
    id: 'event-ar-hunt',
    iconSrc: iconTrophy,
    colorClass: 'green',
    tag: 'Specific Locations',
    title: 'Event AR Hunt',
    blurb: 'Place 3D objects across key locations and invite fans to find them.',
    description:
      'Attendees discover hidden objects on their map, travel to pinned locations around the venue, and uncover 3D objects in AR to receive rewards or prizes.',
    benefits:
      'Encourage venue exploration, boost fan engagement, increase sponsor interaction.',
    features: [
      'Adaptable geolocation radius',
      '0\u2013100 Locations',
      'Geotargeted reward delivery',
    ],
    steps: [
      {
        text: 'Apply your branding, colours, and messaging.',
        iconUrl: stepIcons.star,
      },
      {
        text: 'Upload your own 3D object or choose one from our library.',
        iconUrl: stepIcons.pin,
      },
      {
        text: 'Pinpoint locations across the venue where attendees will discover the objects.',
        iconUrl: stepIcons.heart,
      },
      {
        text: 'Upload the reward or prize attendees will unlock.',
        iconUrl: stepIcons.email,
      },
      {
        text: 'Choose the start and end dates for your AR experience.',
        iconUrl: stepIcons.calendar,
      },
      {
        text: 'Publish your campaign and share the link via SMS, email, or any channel.',
        iconUrl: stepIcons.textbox,
      },
    ],
  },
]

const activeStudy = ref<UseCase | null>(null)
</script>

<style scoped>
/* ── Overlay ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: var(--base-layout-modal-z-index, 10000);
  padding: clamp(2rem, 6vh, 4rem) clamp(1rem, 5vw, 4rem);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

/* ── Container ── */
.modal-overlay .modal-container {
  width: min(980px, 100%);
  max-height: calc(100dvh - 4rem);
  padding: 18px 18px 26px;
  border-radius: 16px;
  background: var(--ui-inverse-surface) !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.modal-container--detail {
  padding: 0;
  background: #a7ff38;
}

/* ══════════════════════════════════════
   GRID VIEW
   ══════════════════════════════════════ */

.panel-header {
  position: relative;
  margin-bottom: 26px;
  padding: 0 3.5rem;
  text-align: center;
  min-height: 2rem;
}

.modal-overlay .modal-container .panel-title {
  margin: 0;
  font-size: clamp(2rem, 3vw, 2.4rem);
  line-height: 1.1;
  font-weight: 700;
  color: #fff !important;
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px;
}

.case-card {
  position: relative;
  min-height: 248px;
  padding: 22px 22px 28px;
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  color: var(--color-background);
  box-shadow: 0 16px 30px rgba(8, 17, 28, 0.22);
  overflow: visible;
  transition: transform 140ms ease, box-shadow 140ms ease;
}

.case-card:hover,
.case-card:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 20px 34px rgba(8, 17, 28, 0.28);
  outline: none;
}

.case-card.green {
  background: #a7ff38;
}

.case-card.blue {
  background: #50e5ff;
}

.case-icon {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 2;
  width: 80px;
  height: 80px;
  transform: translateY(-40%);
  object-fit: contain;
  filter: drop-shadow(0 10px 12px rgba(15, 23, 36, 0.25));
}

.case-card .case-tag {
  display: inline-block;
  margin: 0 0 20px;
  font-size: 1.05rem;
  font-style: italic;
  color: var(--color-background) !important;
}

.case-card .case-title {
  margin: 0 0 12px;
  font-size: 2rem;
  line-height: 1.05;
  font-weight: 700;
  color: var(--color-background) !important;
}

.case-card .case-copy {
  margin: 0;
  max-width: 26ch;
  font-size: 1.02rem;
  line-height: 1.35;
  color: var(--color-background) !important;
}

/* ══════════════════════════════════════
   DETAIL VIEW
   ══════════════════════════════════════ */

.steps-panel {
  width: 100%;
  border-radius: 12px;
  padding: 28px 42px 34px;
}

.steps-panel.green {
  background: #a7ff38;
}

.steps-panel.blue {
  background: #50e5ff;
}

.steps-panel .back-button {
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-background) !important;
  cursor: pointer;
  padding: 0.25rem 0;
  margin-bottom: 12px;
  opacity: 0.7;
  transition: opacity 0.15s ease;
}

.back-button:hover {
  opacity: 1;
}

/* ── Intro Block ── */
.intro-block {
  padding: 4px 0 38px;
}

.steps-panel .intro-title {
  margin: 0 0 26px;
  text-align: center;
  font-size: 2.35rem;
  line-height: 1.1;
  color: var(--color-background) !important;
  font-weight: 700;
}

.steps-panel .intro-copy {
  margin: 0;
  max-width: 790px;
  font-size: 1.05rem;
  line-height: 1.45;
  color: var(--color-background) !important;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 24px 0 34px;
  display: grid;
  gap: 14px;
  max-width: 520px;
}

.steps-panel .feature-item {
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--color-background) !important;
  font-size: 1.05rem;
  line-height: 1.3;
}

.feature-icon {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  object-fit: contain;
}

.steps-panel .intro-note {
  margin: 0;
  max-width: 700px;
  font-size: 1.08rem;
  line-height: 1.35;
  font-style: italic;
  font-weight: 700;
  color: var(--color-background) !important;
}

/* ── Steps Section ── */
.steps-panel .steps-heading {
  margin: 0 0 30px;
  text-align: center;
  font-size: 2rem;
  line-height: 1.1;
  color: var(--color-background) !important;
}

.steps-grid {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.step-card {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 92px;
  padding: 16px 18px;
  border-radius: 22px;
  background: #2f3d50;
  color: #fff;
  box-shadow: 0 14px 30px rgba(11, 20, 32, 0.2);
  text-align: center;
}

.steps-panel .step-card .step-copy {
  margin: 0;
  width: 100%;
  font-size: 1rem;
  line-height: 1.45;
  color: #fff !important;
  text-align: center;
}

/* Step grid area assignments */

/* ── Step Icons ── */

/* ── Connectors ── */

/* ══════════════════════════════════════
   RESPONSIVE
   ══════════════════════════════════════ */

@media (max-width: 860px) {
  .steps-panel {
    padding: 24px 24px 26px;
  }

  .intro-block {
    padding-bottom: 30px;
  }

  .intro-title {
    font-size: 1.85rem;
    margin-bottom: 18px;
  }

  .steps-grid {
    gap: 16px;
  }

  .step-card {
    min-height: 88px;
  }
}

@media (max-width: 720px) {
  .modal-overlay {
    padding: calc(env(safe-area-inset-top, 0px) + 5.5rem) 0.75rem 1.5rem;
  }

  .modal-container {
    width: 100%;
    max-height: calc(100dvh - env(safe-area-inset-top, 0px) - 7rem);
    padding: 18px 14px 24px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .modal-container--detail {
    padding: 0;
    background: #a7ff38;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .panel-header {
    margin-bottom: 18px;
  }

  .cases-grid {
    grid-template-columns: 1fr;
  }

  .case-card {
    min-height: 220px;
  }

  .case-title {
    font-size: 1.75rem;
  }
}
</style>
